import { Box, Button, DropdownMenu, Flex } from '@radix-ui/themes'
import { useRef } from 'react'
import { Form, Link, useSubmit } from 'react-router'
import { DexRadixSize } from '#app/config/ui.config.ts'
import { useBreakpoints } from '#app/hooks/breakpoints.hook.ts'
import { useUserDisplay } from '#app/utils/user-display.ts'
import { userHasRole, useUser } from '#app/utils/user.ts'
import {
  DexIconAvatar,
  DexIconExit,
  DexIconReader,
  DexIconReleaseNotes,
  DexIconUser,
  DexIconWorkspace,
} from '../ui/icons/dex-icons.tsx'

export function UserDropdown({
  onOpenReleaseNotes,
  selfHosted,
}: {
  onOpenReleaseNotes?: () => void
  selfHosted?: boolean
}) {
  const user = useUser()
  const isAdmin = userHasRole(user, 'admin')
  const { getDisplayName } = useUserDisplay()
  const submit = useSubmit()
  const formRef = useRef<HTMLFormElement>(null)
  const { is3xl } = useBreakpoints()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button
          size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
          variant="soft"
        >
          <Flex align="center" gap="2">
            <Flex align="center" gap="1">
              <DexIconUser data-testid="user-photo" />
              <Box className="font-semibold">{getDisplayName(user)}</Box>
            </Flex>
            <DropdownMenu.TriggerIcon />
          </Flex>
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content>
        {isAdmin ? (
          <>
            <DropdownMenu.Item asChild>
              <Link prefetch="intent" to="/admin">
                <DexIconWorkspace /> Admin
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        ) : null}
        <DropdownMenu.Item asChild>
          <Link prefetch="intent" to="/settings/profile">
            <DexIconAvatar /> Profile
          </Link>
        </DropdownMenu.Item>
        {onOpenReleaseNotes ? (
          <DropdownMenu.Item onSelect={onOpenReleaseNotes}>
            <DexIconReleaseNotes /> Release notes
          </DropdownMenu.Item>
        ) : null}
        {!selfHosted && (
          <DropdownMenu.Item asChild>
            <Link prefetch="intent" to="/terms">
              <DexIconReader /> Terms of Service
            </Link>
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item
          asChild
          // this prevents the menu from closing before the form submission is completed
          onSelect={event => {
            event.preventDefault()
            submit(formRef.current)
          }}
        >
          <Form action="/logout" method="POST" ref={formRef}>
            <DexIconExit />
            <button type="submit">Logout</button>
          </Form>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
